import {type Moment} from 'moment-timezone';
import moment from 'moment-timezone';

import type {DateRangeInclusive} from 'web-app/react/schedule-break/helpers/date-range';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {groupByEquivalenceConsecutive} from 'web-app/util/array';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';

import * as Types from '../../../util/graphql/api-types';
import type {InvoiceSummaryQuery} from '../queries/__generated__/invoice-summary.query.api-types';

type ApiInvoiceSummaryPeriod = InvoiceSummaryQuery['finance']['invoiceSummary']['v1']['overview']['periods'][number];

export type PlanUpdate = {
    planId: string;
    range: DateRangeInclusive | null;
    tuitions: TuitionUpdateInput[] | null;
    fees: FeeUpdateInput[] | null;
    discounts: DiscountUpdateInput[] | null;
    pricingGroupId: string | null;
    ruleGroupId: string | null;
    defaultRuleGroup: boolean;
};
export type TuitionUpdateInput = {
    tuitionId: string;
    overridePrice: number | null;
};
export type FeeUpdateInput = {
    feeId: string;
};
export type DiscountUpdateInput = {
    name: string;
    amount: number;
    percent: boolean;
};

export type InvoiceSummary = InvoiceSummaryPeriod[];
export type InvoiceSummaryPeriod = {
    kind: PeriodKind;
    billingFrequency: BillingFrequency;
    planId: string;
    alreadyBilled: boolean;
    isCurrentPlan: boolean;
    isLastInPlan: boolean;
    changesPricingGroup: boolean;
    periodDateRange: DateRangeInclusive;
    customDateRange: {
        fromInclusive: Moment | null;
        toInclusive: Moment | null;
    };
    payables: InvoiceSummaryPayable[];
};
export type InvoiceSummaryPayable = {
    kind: PayableKind;
    name: string;
    price: number;
    quality: PriceQuality;
};

export type PeriodKind = 'plan' | 'break';
export type BillingFrequency = 'weekly' | 'monthly' | 'biweekly';
export type PayableKind = 'tuition' | 'fee' | 'discount' | 'total';
export type PriceQuality = 'exact' | 'prorated' | 'inaccurate';

export type InvoiceSummaryGroup = {
    alreadyInvoicedGroup: InvoiceSummary | null;
    group: InvoiceSummary;
    isCurrentPlan: boolean;
};

/**
 * Groups consecutive periods belonging to the same plan.
 */
export const groupPeriodsByPlan = (periods: InvoiceSummary): InvoiceSummaryGroup[] => {
    const groups = groupByEquivalenceConsecutive(periods, (period1, period2) => period1.planId === period2.planId);
    // Note: The groups are always non-empty.
    // TODO: Using adjacency to determine already invoiced parts seems fragile (see GROWTH-1212)
    const groupPairs = associateLeftBy(groups, (first, second) => first[0].alreadyBilled && !second[0].alreadyBilled);
    return groupPairs.map(pair => ({
        alreadyInvoicedGroup: pair.associated ?? null,
        group: pair.element,
        isCurrentPlan: pair.element[0].isCurrentPlan,
    }));
};

/**
 * Pairs consecutive elements of `as` if they satisfy `isAssociated`. Keeps all other elements as singletons.
 *
 * Greedy.
 *
 * @example
 *   const isPrefix = (prefix : string, s : string):boolean=>s.indexOf(prefix)===0;
 *   associateLeftBy(["hell", "hello", "helloworld", "!"], isPrefix)
 *   ===
 *   [{associated: "hell", element: "hello"}, {element:"helloworld"}, {element:"!"}]
 */
const associateLeftBy = <T>(
    as: T[],
    isAssociated: (associated: T, element: T) => boolean,
): {associated?: T | undefined; element: T}[] => {
    const worker = (
        i: number,
        result: {associated?: T | undefined; element: T}[],
    ): {associated?: T | undefined; element: T}[] => {
        if (i >= as.length) {
            return result;
        }
        if (i + 1 < as.length && isAssociated(as[i], as[i + 1])) {
            return worker(i + 2, [...result, {associated: as[i], element: as[i + 1]}]);
        }
        return worker(i + 1, [...result, {element: as[i]}]);
    };
    return worker(0, []);
};

/**
 * True iff the period is a custom period, i.e. the start or end date does not
 * align with the invoice cadence.
 */
export const isCustomPeriod = (period: InvoiceSummaryPeriod): boolean =>
    hasValue(period.customDateRange.fromInclusive) || hasValue(period.customDateRange.toInclusive);

/**
 * Gets the first date of the period.
 *
 * Will return the custom start date if the period is a custom period.
 */
export const getStartDate = (period: InvoiceSummaryPeriod): Moment =>
    period.customDateRange.fromInclusive ?? period.periodDateRange.fromInclusive;

/**
 * Gets the last date of the period.
 *
 * Will return the custom end date if the period is a custom period.
 */
export const getEndDate = (period: InvoiceSummaryPeriod): Moment | null =>
    period.customDateRange.toInclusive ?? period.periodDateRange.toInclusive;

/** Formats a date range for an invoice summary period. */
export const formatDateRange = (period: InvoiceSummaryPeriod): string => {
    const startDateString = formatDate(getStartDate(period));
    const endDate = getEndDate(period);
    return hasValue(endDate) ? `${startDateString} - ${formatDate(endDate)}` : `${startDateString}-`;
};

export const formatMonth = (date: Moment): string => date.format('MMMM');
const formatDate = (date: Moment): string => date.format('MMM DD');

export const switchPeriodKind = <T>(kind: PeriodKind, onPlan: () => T, onBreak: () => T): T => {
    switch (kind) {
        case 'plan':
            return onPlan();
        case 'break':
            return onBreak();
        default:
            exhaustiveCheck(kind);
            return onPlan();
    }
};

export const transformPlanUpdateToApi = (planUpdate: PlanUpdate): Types.PlanUpdate => ({
    planId: planUpdate.planId,
    range: hasValue(planUpdate.range)
        ? {
              from: planUpdate.range.fromInclusive.format('YYYY-MM-DD'),
              to: hasValue(planUpdate.range.toInclusive) ? planUpdate.range.toInclusive.format('YYYY-MM-DD') : null,
          }
        : null,
    tuitions: hasValue(planUpdate.tuitions)
        ? planUpdate.tuitions.map(tuition => ({
              id: tuition.tuitionId,
              overridePrice: tuition.overridePrice?.toString(),
          }))
        : undefined,
    fees: hasValue(planUpdate.fees) ? planUpdate.fees.map(fee => ({id: fee.feeId})) : null,
    discounts: hasValue(planUpdate.discounts)
        ? planUpdate.discounts.map(discount => ({
              name: discount.name,
              amount: discount.amount,
              percent: discount.percent,
          }))
        : undefined,
    pricingGroupId: planUpdate.pricingGroupId,
    ruleGroupId: planUpdate.ruleGroupId,
    defaultRuleGroup: planUpdate.defaultRuleGroup,
});

export const transformInvoiceSummaryFromApi = (invoiceSummary: ApiInvoiceSummaryPeriod[]): InvoiceSummary => {
    const optionalDate = (date: string | null | undefined): Moment | null => (hasValue(date) ? moment(date) : null);
    return invoiceSummary.map(period => ({
        kind: transformPeriodKindFromApi(period.kind),
        billingFrequency: transformBillingFrequencyFromApi(period.billingFrequency),
        planId: period.planId,
        alreadyBilled: period.alreadyBilled,
        isCurrentPlan: period.isCurrentPlan,
        isLastInPlan: period.isLastInPlan,
        changesPricingGroup: period.changesPricingGroup,
        periodDateRange: {
            fromInclusive: moment(period.periodDateRange.from),
            toInclusive: optionalDate(period.periodDateRange.to),
        },
        customDateRange: {
            fromInclusive: optionalDate(period.customDateRange.from),
            toInclusive: optionalDate(period.customDateRange.to),
        },
        payables: period.payables.map(payable => ({
            kind: transformPayableKindFromApi(payable.kind),
            name: payable.name,
            price: payable.price.price,
            quality: transformPriceQualityFromApi(payable.price.quality),
        })),
    }));
};

/** TODO: error handling */
const transformBillingFrequencyFromApi = (billingFrequency: Types.BillingFrequency): BillingFrequency => {
    switch (billingFrequency) {
        case Types.BillingFrequency.Monthly:
            return 'monthly';
        case Types.BillingFrequency.Weekly:
            return 'weekly';
        case Types.BillingFrequency.Biweekly:
            return 'biweekly';
        default:
            return 'monthly';
    }
};

/** TODO: error handling */
const transformPeriodKindFromApi = (periodKind: Types.PeriodKind): PeriodKind => {
    switch (periodKind) {
        case Types.PeriodKind.plan:
            return 'plan';
        case Types.PeriodKind.break:
            return 'break';
        default:
            return 'plan';
    }
};

/** TODO: error handling */
const transformPayableKindFromApi = (payableKind: Types.PayableKind): PayableKind => {
    switch (payableKind) {
        case Types.PayableKind.tuition:
            return 'tuition';
        case Types.PayableKind.fee:
            return 'fee';
        case Types.PayableKind.discount:
            return 'discount';
        case Types.PayableKind.total:
            return 'total';
        default:
            return 'total';
    }
};

/** TODO: error handling */
const transformPriceQualityFromApi = (priceQuality: Types.PriceQuality): PriceQuality => {
    switch (priceQuality) {
        case Types.PriceQuality.exact:
            return 'exact';
        case Types.PriceQuality.prorated:
            return 'prorated';
        case Types.PriceQuality.inaccurate:
            return 'inaccurate';
        default:
            return 'inaccurate';
    }
};
